import { AddIcon, EditIcon } from '@chakra-ui/icons';
import { HStack, IconButton, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useGetAssignmentsCompanies from './hooks/useGetAssignmentsData';
import AddAssignmentModal from './modals/AddAssignmentModal';
import useGetRegistrars from './queries/useGetRegistrars';

import { Button, Text } from '@/components/v4';
import { Error } from 'components/index';
import ActionMenu from 'components/presentational/ActionMenu';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import { CustomSelect, Option } from 'components/v4/CustomSelect';
import Tooltip from 'components/v4/Tooltip';
import FeatureDisabledPage from 'pages/resources/feature-disabled-page';

import appConfig from '@/config.json';
import { formatRegistrationDuration, protocolOptions } from './constants';
import { MdAssignment } from 'react-icons/md';

// NOTE: Registrars is the same as PBX, folder naming pbx for route convention
export default function Registrars() {
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { filter: locationFilter = 'Company' } = location.state || {};
    const companyScope = appConfig.scope.company;

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany, currentPartner } = useSelector(
        (state) => state.navigationLists,
    );
    const { requiredScope } = useSelector((state) => state.login);
    const { restError } = useSelector((state) => state.modal);
    const isNotEnabled = restError?.includes('not enabled') || false;

    // Modal state for assignment
    const {
        isOpen: isAssignModalOpen,
        onOpen: onAssignModalOpen,
        onClose: onAssignModalClose,
    } = useDisclosure();
    const [selectedRegistrarId, setSelectedRegistrarId] = useState(null);

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    // Filter state for ownership-based filtering
    const [filter, setFilter] = useState('Company');
    const [filteredData, setFilteredData] = useState([]);
    const isCompany = filter === 'Company';

    const [companies, setCompanies] = useState([]);

    const { isLoading: companiesLoading, isFetching: companiesFetching } =
        useGetAssignmentsCompanies(
            currentPartner,
            requiredScope,
            setCompanies,
            true,
        );

    const handleEditRegistrar = useCallback(
        (registrarData) => {
            navigate(`/pbx/edit/${registrarData.id}`, {
                state: {
                    filter,
                },
            });
        },
        [navigate, filter],
    );

    const handleAssignRegistrar = useCallback(
        (registrarData) => {
            // Comment out the navigate feature - bypass assignment page
            // navigate(`/pbx/assign/${registrarData.id}`, { state: { filter } });

            // Set the selected registrar and open the modal instead
            setSelectedRegistrarId(registrarData.id);
            onAssignModalOpen();
        },
        [onAssignModalOpen],
    );

    const handleAddRegistrar = () => {
        navigate('/pbx/add', { state: { filter } });
    };

    const handleFilterChange = (selectedFilter) => {
        setFilter(selectedFilter);
    };

    const handleAssignmentSuccess = () => {
        // Refetch data to update the table after successful assignment
        refetch();
        onAssignModalClose();
        setSelectedRegistrarId(null);
    };

    const handleAssignmentModalClose = () => {
        onAssignModalClose();
        setSelectedRegistrarId(null);
    };

    // Determine if we should show filter dropdown
    const shouldShowFilter = requiredScope > companyScope;

    const filterOptions = [
        {
            label: 'Company',
            value: 'Company',
        },
        {
            label: 'Partner',
            value: 'Partner',
        },
    ];

    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 250,
            },
            {
                headerName: 'Username',
                field: 'username',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.username,
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'URI',
                field: 'uri',
                cellRenderer: CopyCellRenderer,
                flex: 1.5,
                minWidth: 180,
            },
            {
                headerName: 'Proxy',
                field: 'proxy',
                cellRenderer: CopyCellRenderer,
                flex: 1.5,
                minWidth: 180,
            },
            {
                headerName: 'Protocol',
                field: 'protocol',
                cellRenderer: CopyCellRenderer,
                flex: 1,
                minWidth: 100,
                valueGetter: (params) => {
                    const protocol = params.data?.protocol;
                    const protocolOption = protocolOptions.find(
                        (p) => p.value === protocol,
                    );
                    return protocolOption?.label;
                },
            },
            {
                headerName: 'Registration Duration',
                field: 'registrationDuration',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => {
                    const duration = params.data.registrationDuration;
                    return formatRegistrationDuration(duration);
                },
                flex: 1.2,
                minWidth: 150,
            },
            // {
            //     headerName: 'Assignments',
            //     field: 'assignments',
            //     cellRenderer: CopyCellRenderer,
            //     valueGetter: (params) => {
            //         if (Array.isArray(params.data.assignments))
            //             return params.data.assignments.length;
            //     },
            //     hide: filter === 'Company',
            //     flex: 1.2,
            //     minWidth: 150,
            // },
            {
                maxWidth: 100,
                headerName: 'Action',
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => {
                    if (filter === 'Company') {
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                }}>
                                <HStack
                                    spacing={0}
                                    justifyContent="center"
                                    alignItems="center"
                                    width="100%"
                                    height="100%">
                                    <Tooltip label="Edit Registrar">
                                        <IconButton
                                            icon={
                                                <BsThreeDotsVertical
                                                    fill={
                                                        darkMode
                                                            ? 'white'
                                                            : 'black'
                                                    }
                                                />
                                            }
                                            aria-label="Edit"
                                            variant="ghost"
                                            onClick={() =>
                                                handleEditRegistrar(params.data)
                                            }
                                            _hover={{
                                                bg: darkMode
                                                    ? 'whiteAlpha.200'
                                                    : 'blackAlpha.100',
                                            }}
                                            _active={{
                                                bg: darkMode
                                                    ? 'whiteAlpha.300'
                                                    : 'blackAlpha.200',
                                            }}
                                            _focus={{ boxShadow: 'none' }}
                                        />
                                    </Tooltip>
                                </HStack>
                            </div>
                        );
                    }

                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => handleEditRegistrar(params.data),
                            icon: EditIcon,
                        },
                    ];

                    // Add Assign action if filter is Partner (ownership 40)
                    if (filter === 'Partner') {
                        actionItems.push({
                            label: 'Assign',
                            onClick: () => handleAssignRegistrar(params.data),
                            icon: MdAssignment,
                        });
                    }

                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [darkMode, handleEditRegistrar, handleAssignRegistrar, filter],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    useEffect(() => {
        if (locationFilter) {
            setFilter(locationFilter);
        }
    }, [locationFilter]);

    // Filter data based on ownership
    useEffect(() => {
        if (!tableData.data || tableData.data.length === 0) {
            setFilteredData([]);
            return;
        }

        const ownershipValue = isCompany ? 20 : 40;
        const filtered = tableData.data.filter(
            (registrar) => registrar.ownership === ownershipValue,
        );
        if (ownershipValue === 40) {
            const enhancedPartnerRegistrars = filtered.map((r) => ({
                ...r,
                assignments: tableData.data.filter(
                    (data) => r.id === data.parentID && data.ownership === 20,
                ),
            }));
            setFilteredData(enhancedPartnerRegistrars);
        } else {
            setFilteredData(filtered);
        }
        gridRef?.current?.api?.applyColumnState({
            state: [
                {
                    colId: 'name',
                    sort: 'asc',
                },
            ],
            defaultState: {
                sort: null,
            },
        });
    }, [tableData.data, filter, isCompany]);

    // Update table data with filtered results
    const displayTableData = useMemo(
        () => ({
            columns,
            data: filteredData,
        }),
        [columns, filteredData],
    );

    const {
        refetch,
        isRefetching,
        isFetching,
        error: registrarsError,
    } = useGetRegistrars(setTableData, gridRef, baseColumns, currentCompany);

    const titleName = 'Registrars';

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (isNotEnabled || registrarsError) {
        return <FeatureDisabledPage />;
    }

    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Registrar Management
            </Text>

            <AGGridMain
                gridRef={gridRef}
                tableData={displayTableData}
                isFetching={isFetching}
                isRefetching={isRefetching}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={handleAddRegistrar}
                            size="sm">
                            Add Registrar
                        </Button>

                        {shouldShowFilter && (
                            <HStack
                                sx={{
                                    '& select': {
                                        borderRadius: '6px',
                                    },
                                }}>
                                <span className="text-sm font-medium">
                                    Filter:
                                </span>
                                <CustomSelect
                                    value={filter}
                                    onChange={handleFilterChange}
                                    width="150px"
                                    size="sm"
                                    h="32px">
                                    {filterOptions.map(({ label, value }) => (
                                        <Option key={label} value={value}>
                                            {label}
                                        </Option>
                                    ))}
                                </CustomSelect>
                            </HStack>
                        )}
                    </HStack>
                }
            />

            {/* Add Assignment Modal - bypasses assignment page entirely (ADO #618)*/}
            <AddAssignmentModal
                isOpen={isAssignModalOpen}
                onClose={handleAssignmentModalClose}
                registrarId={selectedRegistrarId}
                companies={companies}
                isLoading={companiesLoading}
                isFetching={companiesFetching}
                onSuccess={handleAssignmentSuccess}
            />
        </>
    );
}
